import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  KeyIcon,
  UsersIcon,
  ComputerDesktopIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  CalendarDaysIcon,
  ShieldCheckIcon,
  TicketIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { Button, Card, CardContent, CardHeader, CardTitle } from '../../components/ui';
import { MetricCard } from '../../components/ui/MetricCard';
import { Badge } from '../../components/ui/Badge';
import { UsageBar } from '../../components/dashboard/UsageBar';
import { ClientUsageTable } from '../../components/dashboard/ClientUsageTable';
import { statisticsService } from '../../services/statisticsService';
import { licenseService } from '../../services/licenseService';
import { formatDate } from '../../utils/formatters';
import {
  USAGE_SORT_OPTIONS,
  filterUsageClients,
  getUsagePercent,
  sortUsageClients,
  sumUsage,
} from '../../utils/usage';
import { downloadUsageReportPdf } from '../../utils/usageReport';
import { ROUTES } from '../../constants';

/** La API devuelve `data` como array; algunos clientes esperaban `results`. */
const normalizeLicenseList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload?.results && Array.isArray(payload.results)) return payload.results;
  return [];
};

const EXPIRING_WINDOW_DAYS = 30;
const RECENT_VALIDATIONS_LIMIT = 10;

const getValidationResultLabel = (result) => {
  const labels = {
    success: 'Exitosa',
    failed: 'Fallida',
    expired: 'Expirada',
    suspended: 'Suspendida',
    limit_exceeded: 'Límite excedido',
  };
  return labels[result] || result || '—';
};

const getValidationResultBadgeVariant = (result) => {
  switch (result) {
    case 'success':
      return 'success';
    case 'failed':
      return 'danger';
    case 'expired':
    case 'suspended':
      return 'warning';
    case 'limit_exceeded':
      return 'danger';
    default:
      return 'secondary';
  }
};

const getValidationEventTypeLabel = (eventType) => {
  const labels = {
    new_installation: 'Nueva instalación',
    revalidation: 'Revalidación',
    installation_id_mac_conflict: 'Conflicto ID / MAC',
    heartbeat: 'Heartbeat',
    legacy: 'Histórico',
  };
  return labels[eventType] || eventType || '—';
};

/**
 * El backend aplica el rango por fecha de calendario en TIME_ZONE (param
 * `expiring_within_days`), así no falla el parseo de ISO en query strings ni se
 * excluye "vence hoy" por usar la hora actual como límite inferior.
 */
const expiringLicensesQueryParams = () => ({
  expiring_within_days: EXPIRING_WINDOW_DAYS,
  ordering: 'expiry_date',
});

/** Días de calendario locales hasta el vencimiento (0 = hoy), alineado con lo que ve el usuario. */
const daysUntil = (isoDate) => {
  if (!isoDate) return null;
  const end = new Date(isoDate);
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((endDay - today) / 86400000);
};

export const DashboardPage = () => {
  const [usageScope, setUsageScope] = useState('valid');
  const [usageSearch, setUsageSearch] = useState('');
  const [usageSort, setUsageSort] = useState('free_desc');
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfError, setPdfError] = useState('');

  const {
    data: usageData,
    isLoading: usageLoading,
    isError: usageError,
  } = useQuery({
    queryKey: ['dashboard-usage', usageScope],
    queryFn: () => statisticsService.getUsage({ scope: usageScope }),
    refetchInterval: 60000,
  });

  const { data: recentLicensesData, isLoading: recentLoading } = useQuery({
    queryKey: ['dashboard-recent-licenses'],
    queryFn: () => licenseService.getLicenses({ ordering: '-created_at' }),
    refetchInterval: 60000,
  });

  const {
    data: expiringLicensesData,
    isLoading: expiringLoading,
    isError: expiringError,
  } = useQuery({
    queryKey: ['dashboard-expiring-licenses', EXPIRING_WINDOW_DAYS],
    queryFn: () => licenseService.getLicenses(expiringLicensesQueryParams()),
    refetchInterval: 60000,
  });

  const {
    data: recentValidationsData,
    isLoading: validationsLoading,
    isError: validationsError,
  } = useQuery({
    queryKey: ['dashboard-recent-validations', RECENT_VALIDATIONS_LIMIT],
    queryFn: () =>
      licenseService.getValidationLogs({
        limit: RECENT_VALIDATIONS_LIMIT,
      }),
    refetchInterval: 60000,
  });

  const usageClients = useMemo(
    () =>
      sortUsageClients(
        filterUsageClients(usageData?.clients || [], usageSearch),
        usageSort
      ),
    [usageData, usageSearch, usageSort]
  );
  const visibleTotals = useMemo(() => sumUsage(usageClients), [usageClients]);
  const globalTotals = usageData?.totals || sumUsage([]);

  const handleDownloadPdf = async () => {
    setPdfError('');
    setPdfGenerating(true);
    try {
      await downloadUsageReportPdf({
        clients: usageClients,
        totals: visibleTotals,
        scope: usageScope,
        search: usageSearch.trim(),
      });
    } catch (error) {
      console.error('Error al generar PDF:', error);
      setPdfError('No se pudo generar el PDF. Intenta nuevamente.');
    } finally {
      setPdfGenerating(false);
    }
  };

  const recentLicenses = normalizeLicenseList(recentLicensesData).slice(0, 5);
  const expiringLicenses = normalizeLicenseList(expiringLicensesData).slice(0, 8);
  const recentValidations = normalizeLicenseList(recentValidationsData).slice(
    0,
    RECENT_VALIDATIONS_LIMIT
  );

  const urgencyBadgeVariant = (days) => {
    if (days == null) return 'secondary';
    if (days <= 7) return 'danger';
    if (days <= EXPIRING_WINDOW_DAYS) return 'warning';
    return 'secondary';
  };

  // Función para obtener el color del badge según el estado
  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'trial':
        return 'warning';
      case 'expired':
        return 'danger';
      case 'suspended':
        return 'danger';
      case 'cancelled':
        return 'danger';
      default:
        return 'default';
    }
  };

  // Función para obtener el texto del estado
  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Activa';
      case 'trial':
        return 'Trial';
      case 'expired':
        return 'Expirada';
      case 'suspended':
        return 'Suspendida';
      case 'cancelled':
        return 'Cancelada';
      default:
        return status;
    }
  };

  const scopeLabel =
    usageScope === 'all' ? 'Todas las licencias' : 'Solo licencias activas y vigentes';

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">
            Uso de licencias por cliente: licencias contratadas e instalaciones realizadas
          </p>
          {usageData?.generated_at && (
            <p className="text-sm text-gray-500 mt-1">
              Última actualización: {formatDate(usageData.generated_at, 'datetime')}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          <Button
            variant="primary"
            onClick={handleDownloadPdf}
            loading={pdfGenerating}
            disabled={usageLoading || usageError || usageClients.length === 0}
          >
            {!pdfGenerating && <ArrowDownTrayIcon className="h-5 w-5 mr-2" />}
            Descargar resumen PDF
          </Button>
          {pdfError && <p className="text-xs text-danger-600">{pdfError}</p>}
        </div>
      </div>

      {/* Metrics Grid */}
      {usageLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="card p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
              <div className="h-8 bg-gray-200 rounded w-1/2 mb-2" />
              <div className="h-3 bg-gray-200 rounded w-1/3" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricCard
            title="Clientes con licencias"
            value={globalTotals.clients.toLocaleString()}
            icon={<UsersIcon className="h-8 w-8" />}
          />
          <MetricCard
            title="Códigos de licencia"
            value={globalTotals.license_codes.toLocaleString()}
            subtitle="Contratos emitidos"
            icon={<KeyIcon className="h-8 w-8" />}
          />
          <MetricCard
            title="Licencias contratadas"
            value={globalTotals.seats.toLocaleString()}
            subtitle="Instalaciones permitidas"
            icon={<TicketIcon className="h-8 w-8" />}
          />
          <MetricCard
            title="Instalaciones realizadas"
            value={globalTotals.installed.toLocaleString()}
            subtitle={
              <div className="space-y-1">
                <UsageBar
                  seats={globalTotals.seats}
                  installed={globalTotals.installed}
                  showLabel={false}
                />
                <span>
                  {globalTotals.free.toLocaleString()} cupos libres ·{' '}
                  {getUsagePercent(globalTotals.seats, globalTotals.installed)} % de uso
                </span>
              </div>
            }
            icon={<ComputerDesktopIcon className="h-8 w-8" />}
          />
        </div>
      )}

      {/* Uso por cliente */}
      <Card className="border border-gray-200 shadow-sm">
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 border-b border-gray-100 pb-3">
          <div>
            <CardTitle className="text-base font-semibold text-gray-900">
              Uso por cliente
            </CardTitle>
            <p className="text-xs text-gray-500 mt-1">
              {scopeLabel} · Desplegá un cliente para ver sus software y códigos
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                value={usageSearch}
                onChange={(e) => setUsageSearch(e.target.value)}
                placeholder="Buscar cliente..."
                className="w-52 rounded-md border border-gray-300 py-1.5 pl-8 pr-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
            <select
              value={usageSort}
              onChange={(e) => setUsageSort(e.target.value)}
              className="rounded-md border border-gray-300 py-1.5 pl-2 pr-8 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              aria-label="Ordenar"
            >
              {USAGE_SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <select
              value={usageScope}
              onChange={(e) => setUsageScope(e.target.value)}
              className="rounded-md border border-gray-300 py-1.5 pl-2 pr-8 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              aria-label="Licencias incluidas"
            >
              <option value="valid">Activas y vigentes</option>
              <option value="all">Todas</option>
            </select>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {usageLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-3/4" />
                </div>
              ))}
            </div>
          ) : usageError ? (
            <div className="text-center py-8 px-4">
              <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-warning-500" />
              <p className="mt-2 text-sm font-medium text-gray-900">
                No se pudo cargar el uso de licencias
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Comprueba la conexión y que el backend exponga{' '}
                <code className="font-mono bg-gray-100 px-1 rounded">
                  /licenses/api/statistics/usage/
                </code>
                .
              </p>
            </div>
          ) : usageClients.length > 0 ? (
            <ClientUsageTable clients={usageClients} totals={visibleTotals} />
          ) : (
            <div className="text-center py-10 px-4">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                <UsersIcon className="h-6 w-6 text-gray-500" aria-hidden />
              </div>
              <h3 className="mt-3 text-sm font-semibold text-gray-900">
                {usageSearch ? 'Sin resultados' : 'Sin licencias para mostrar'}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {usageSearch
                  ? `Ningún cliente coincide con "${usageSearch}".`
                  : 'No hay licencias que cumplan el criterio seleccionado.'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Licencias recientes y próximas a expirar */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card className="border border-gray-200 shadow-sm">
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-3">
            <CardTitle className="text-base font-semibold text-gray-900">
              Licencias recientes
            </CardTitle>
            <Link
              to={ROUTES.LICENSES}
              className="text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              Ver todas
            </Link>
          </CardHeader>
          <CardContent className="pt-4">
            {recentLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-3/4" />
                  </div>
                ))}
              </div>
            ) : recentLicenses.length > 0 ? (
              <div className="overflow-x-auto -mx-1">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Clave
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Cliente
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Software
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Estado
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Emisión
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {recentLicenses.map((license) => (
                      <tr key={license.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2.5 font-mono text-gray-900 whitespace-nowrap">
                          {license.license_key}
                        </td>
                        <td className="px-3 py-2.5 text-gray-900 whitespace-nowrap">
                          {license.client_name || license.client?.name || '—'}
                        </td>
                        <td className="px-3 py-2.5 text-gray-900 whitespace-nowrap">
                          {license.software_name || license.software?.name || '—'}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          <Badge
                            variant={getStatusBadgeVariant(license.status)}
                            className="text-xs"
                          >
                            {getStatusText(license.status)}
                          </Badge>
                        </td>
                        <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap">
                          {license.issued_date
                            ? formatDate(license.issued_date, 'short')
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-10 px-4">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                  <CheckCircleIcon className="h-6 w-6 text-gray-500" aria-hidden />
                </div>
                <h3 className="mt-3 text-sm font-semibold text-gray-900">No hay licencias</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Comienza creando tu primera licencia.
                </p>
                <Link
                  to={ROUTES.LICENSES}
                  className="mt-4 inline-block text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  Ir a licencias
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border border-gray-200 shadow-sm">
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-3">
            <CardTitle className="text-base font-semibold text-gray-900">
              Próximas a expirar
            </CardTitle>
            <span className="text-xs text-gray-500">
              Próximos {EXPIRING_WINDOW_DAYS} días
            </span>
          </CardHeader>
          <CardContent className="pt-4">
            {expiringLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-3/4" />
                  </div>
                ))}
              </div>
            ) : expiringError ? (
              <div className="text-center py-8 px-4">
                <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-warning-500" />
                <p className="mt-2 text-sm font-medium text-gray-900">
                  No se pudieron cargar las licencias próximas a vencer
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Comprueba la conexión y que el backend esté actualizado (parámetro{' '}
                  <code className="font-mono bg-gray-100 px-1 rounded">expiring_within_days</code>
                  ).
                </p>
              </div>
            ) : expiringLicenses.length > 0 ? (
              <div className="overflow-x-auto -mx-1">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Clave
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Cliente
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Vencimiento
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Restante
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Uso
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {expiringLicenses.map((license) => {
                      const d = daysUntil(license.expiry_date);
                      return (
                        <tr key={license.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2.5 font-mono text-gray-900 whitespace-nowrap">
                            {license.license_key}
                          </td>
                          <td className="px-3 py-2.5 text-gray-900 whitespace-nowrap">
                            {license.client_name || license.client?.name || '—'}
                          </td>
                          <td className="px-3 py-2.5 text-gray-700 whitespace-nowrap">
                            {license.expiry_date
                              ? formatDate(license.expiry_date, 'short')
                              : '—'}
                          </td>
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            {d != null ? (
                              <Badge
                                variant={urgencyBadgeVariant(d)}
                                className="text-xs"
                              >
                                {d === 0
                                  ? 'Hoy'
                                  : d === 1
                                    ? '1 día'
                                    : `${d} días`}
                              </Badge>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            <UsageBar
                              seats={license.max_installations || 0}
                              installed={license.current_installations || 0}
                              className="min-w-[110px]"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-10 px-4">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                  <CalendarDaysIcon className="h-6 w-6 text-gray-500" aria-hidden />
                </div>
                <h3 className="mt-3 text-sm font-semibold text-gray-900">
                  Sin vencimientos próximos
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  No hay licencias activas que venzan en los próximos {EXPIRING_WINDOW_DAYS}{' '}
                  días, o no tienen fecha de expiración.
                </p>
                <Link
                  to={ROUTES.LICENSES}
                  className="mt-4 inline-block text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  Ver licencias
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Últimas licencias validadas */}
      <Card className="border border-gray-200 shadow-sm">
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-3">
          <CardTitle className="text-base font-semibold text-gray-900">
            Últimas licencias validadas
          </CardTitle>
          <span className="text-xs text-gray-500">
            Últimas {RECENT_VALIDATIONS_LIMIT} validaciones
          </span>
        </CardHeader>
        <CardContent className="pt-4">
          {validationsLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-3/4" />
                </div>
              ))}
            </div>
          ) : validationsError ? (
            <div className="text-center py-8 px-4">
              <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-warning-500" />
              <p className="mt-2 text-sm font-medium text-gray-900">
                No se pudieron cargar las validaciones recientes
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Comprueba la conexión y que el backend exponga{' '}
                <code className="font-mono bg-gray-100 px-1 rounded">
                  /licenses/api/validation-logs/
                </code>
                .
              </p>
            </div>
          ) : recentValidations.length > 0 ? (
            <div className="overflow-x-auto -mx-1">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Fecha
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Clave
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Cliente
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Software
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Evento
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Resultado
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Equipo
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {recentValidations.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2.5 text-gray-700 whitespace-nowrap">
                        {log.validation_date
                          ? formatDate(log.validation_date, 'datetime')
                          : '—'}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-gray-900 whitespace-nowrap">
                        {log.license_key || '—'}
                      </td>
                      <td className="px-3 py-2.5 text-gray-900 whitespace-nowrap">
                        {log.client_name || '—'}
                      </td>
                      <td className="px-3 py-2.5 text-gray-900 whitespace-nowrap">
                        {log.software_name || '—'}
                      </td>
                      <td className="px-3 py-2.5 text-gray-700 whitespace-nowrap">
                        {getValidationEventTypeLabel(log.event_type)}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <Badge
                          variant={getValidationResultBadgeVariant(
                            log.validation_result
                          )}
                          className="text-xs"
                        >
                          {getValidationResultLabel(log.validation_result)}
                        </Badge>
                      </td>
                      <td className="px-3 py-2.5 text-gray-700 whitespace-nowrap">
                        <div className="flex flex-col">
                          <span>{log.machine_name || '—'}</span>
                          {log.mac_address ? (
                            <span className="font-mono text-xs text-gray-500">
                              {log.mac_address}
                            </span>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-10 px-4">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
                <ShieldCheckIcon className="h-6 w-6 text-gray-500" aria-hidden />
              </div>
              <h3 className="mt-3 text-sm font-semibold text-gray-900">
                Sin validaciones registradas
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Cuando los clientes validen licencias, aparecerán aquí.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

